import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { STRINGS, SUPPORTED_LANGS, DEFAULT_LANG } from "./strings";
import { Check, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const LS_KEY = "sif.lang";

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return DEFAULT_LANG;
    const stored = localStorage.getItem(LS_KEY);
    if (SUPPORTED_LANGS.includes(stored)) return stored;
    const browser = (navigator.languages || [navigator.language || ""])
      .map((l) => (l || "").slice(0, 2).toLowerCase())
      .find((l) => SUPPORTED_LANGS.includes(l));
    return browser || DEFAULT_LANG;
  });

  useEffect(() => {
    if (typeof document !== "undefined") document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next) => {
    if (!SUPPORTED_LANGS.includes(next)) return;
    localStorage.setItem(LS_KEY, next);
    setLangState(next);
  }, []);

  const toggle = useCallback(() => {
    const i = SUPPORTED_LANGS.indexOf(lang);
    setLang(SUPPORTED_LANGS[(i + 1) % SUPPORTED_LANGS.length]);
  }, [lang, setLang]);

  const t = useCallback(
    (key) => {
      const dict = STRINGS[lang] || STRINGS[DEFAULT_LANG];
      return dict[key] ?? STRINGS[DEFAULT_LANG][key] ?? key;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useT must be used inside LanguageProvider");
  return ctx;
}

/**
 * Tiny inline SVG flags. Drawn at 20x14 (aspect 10:7) to stay accurate.
 * Avoids unicode-flag-emoji which renders inconsistently across OSes.
 */
function FlagUK({ size = 18 }) {
  return (
    <svg width={size} height={(size * 14) / 20} viewBox="0 0 60 42" aria-hidden="true">
      <rect width="60" height="42" fill="#012169" />
      <path d="M0 0 L60 42 M60 0 L0 42" stroke="#FFFFFF" strokeWidth="6" />
      <path d="M0 0 L60 42 M60 0 L0 42" stroke="#C8102E" strokeWidth="3" />
      <rect x="25" width="10" height="42" fill="#FFFFFF" />
      <rect y="16" width="60" height="10" fill="#FFFFFF" />
      <rect x="27" width="6" height="42" fill="#C8102E" />
      <rect y="18" width="60" height="6" fill="#C8102E" />
    </svg>
  );
}

function FlagIT({ size = 18 }) {
  return (
    <svg width={size} height={(size * 14) / 20} viewBox="0 0 60 42" aria-hidden="true">
      <rect width="20" height="42" fill="#009246" />
      <rect x="20" width="20" height="42" fill="#FFFFFF" />
      <rect x="40" width="20" height="42" fill="#CE2B37" />
    </svg>
  );
}

function FlagFR({ size = 18 }) {
  return (
    <svg width={size} height={(size * 14) / 20} viewBox="0 0 60 42" aria-hidden="true">
      <rect width="20" height="42" fill="#002395" />
      <rect x="20" width="20" height="42" fill="#FFFFFF" />
      <rect x="40" width="20" height="42" fill="#ED2939" />
    </svg>
  );
}

function FlagES({ size = 18 }) {
  return (
    <svg width={size} height={(size * 14) / 20} viewBox="0 0 60 42" aria-hidden="true">
      <rect width="60" height="42" fill="#AA151B" />
      <rect y="10.5" width="60" height="21" fill="#F1BF00" />
    </svg>
  );
}

const LANG_META = {
  en: { Flag: FlagUK, name: "English" },
  it: { Flag: FlagIT, name: "Italiano" },
  fr: { Flag: FlagFR, name: "Français" },
  es: { Flag: FlagES, name: "Español" },
};

export function LanguageSwitcher() {
  const { lang, setLang, t } = useT();
  const { Flag: Current } = LANG_META[lang];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={t("lang.label")}
        title={t("lang.label")}
        data-testid="lang-toggle"
        className="flex items-center gap-1.5 px-2 py-1.5 text-xs font-mono uppercase tracking-wider border border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors outline-none"
      >
        <Current size={14} />
        <span>{lang}</span>
        <ChevronDown className="h-3 w-3 text-slate-400" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="rounded-none min-w-[9rem]" data-testid="lang-menu">
        {SUPPORTED_LANGS.map((code) => {
          const { Flag, name } = LANG_META[code];
          return (
            <DropdownMenuItem
              key={code}
              onSelect={() => setLang(code)}
              data-testid={`lang-option-${code}`}
              className={["rounded-none gap-2 text-xs cursor-pointer", code === lang ? "font-semibold" : ""].join(" ")}
            >
              <Flag size={14} />
              <span className="flex-1">{name}</span>
              {code === lang && <Check className="h-3 w-3" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
